import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {ActionList, Button, Flash, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useEffect, useRef, useState} from 'react'
import {clsx} from 'clsx'

import {MergeAction, MergeQueueMethod} from '../../../types'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {mergeQueueButtonText} from '../../../helpers/merge-button-text'
import {StopIcon} from '@primer/octicons-react'
import {
  type DirectMergeActionsSectionProps,
  DirectMergeActionsSection,
  type MergeButtonFocusProps,
} from './DirectMergeActionsSection'
import {MergeDropdownOption} from './MergeDropdownOption'
import {PreviewFeedbackMenu} from './PreviewFeedbackMenu'
import type {
  MergeStateStatus,
  ViewerMergeActions,
  MergeQueue,
  AutoMergeRequest,
  PullRequestMergeRequirementsState,
  AdvisoryWorkspace,
} from '../../../types'
import {useDisableAutoMergeMutation} from '../../../hooks/mutations/use-disable-auto-merge-mutation'
import {useEnableAutoMergeMutation} from '../../../hooks/mutations/use-enable-auto-merge-mutation'
import {BypassMergeRequirementsToggle} from './BypassMergeRequirementsToggle'
import type {
  PullRequestMergeRequirementsPayload,
  JSONAPIPullRequestPayload,
} from '../../../page-data/payloads/merge-box'
import type {Status} from '../../../helpers/mergeability-status'
import {MergeSectionActions} from './MergeSectionActions'
import {calculateMergeability} from '../../../helpers/mergeability-helpers'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'
import {useEnqueueStackMutation} from '../../../hooks/mutations/use-enqueue-stack-mutation'
import {MergeError} from '../../../helpers/merge-error'
import {FlashError} from '@github-ui/flash-error'
import type {StackSectionEntryType} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

const AUTOMERGE_DOCS_LINK =
  'https://docs.github.com/en/pull-requests/collaborating-with-pull-requests/incorporating-changes-from-a-pull-request/automatically-merging-a-pull-request'

export type AddToMergeQueueSectionProps = {
  autoMergeRequest: AutoMergeRequest
  baseRefName: string
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  isDraft: boolean
  isInMergeQueue: boolean
  isStacked?: boolean
  mergeQueue: MergeQueue
  mergeRequirementsState: PullRequestMergeRequirementsState
  mergeStateStatus: MergeStateStatus
  mergeBoxRollupStatus: MergeBoxRollupStatus
  stackedEntriesToMergeCount?: number
  status: Status
  viewerCanAddAndRemoveFromMergeQueue: JSONAPIPullRequestPayload['viewerCanAddAndRemoveFromMergeQueue']
  viewerCanAddToMergeQueueSolo: JSONAPIPullRequestPayload['viewerCanAddToMergeQueueSolo']
  stackMetadata?: PullRequestUserActionStack
  stackPositionInStack?: number
  stackSize?: number
}

/**
 * Renders the merge queue button and provides the ability to select from the merge queue methods
 */
function AddToMergeQueueSection({
  autoMergeRequest,
  baseRefName,
  handleConfirmingMergeInfo,
  mergeCurrentlyBlockedForStacks = false,
  isDraft,
  isInMergeQueue,
  isStacked,
  mergeQueue,
  mergeRequirementsState,
  mergeStateStatus,
  mergeBoxRollupStatus,
  stackedEntriesToMergeCount = 1,
  status,
  viewerCanAddAndRemoveFromMergeQueue,
  viewerCanAddToMergeQueueSolo,
  stackMetadata,
  stackPositionInStack,
  stackSize,
  shouldFocusPrimaryMergeButton,
  setShouldFocusPrimaryMergeButton,
}: AddToMergeQueueSectionProps & MergeButtonFocusProps) {
  const mergeQueueUrl = mergeQueue?.url
  const isSidePanel = useIsMergeBoxSidePanel()

  const [selectedMergeQueueOption, setSelectedMergeQueueOption] = useState<MergeQueueMethod>(MergeQueueMethod.GROUP)
  const [showConfirmMergeWhenReady, setShowConfirmMergeWhenReady] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [ruleErrors, setRuleErrors] = useState<string[]>([])
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: enableAutoMergeMutation, isPending: isEnableAutoMergePending} = useEnableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })

  const {mutate: enqueueStackMutation, isPending: isEnqueueStackPending} = useEnqueueStackMutation({
    onSuccess: () => {
      handleConfirmingMergeInfo(false)
    },
    onError: (e: Error) => {
      if (e instanceof MergeError && e.ruleErrors.length > 0) {
        setRuleErrors(e.ruleErrors)
        setErrorMessage(undefined)
      } else {
        setRuleErrors([])
        setErrorMessage(e.message)
      }
    },
  })

  const isPending = isEnableAutoMergePending || isEnqueueStackPending
  const shouldSendClickAnalyticsEvent = isStacked && stackMetadata && stackPositionInStack && stackSize

  const enqueuePullRequest = () => {
    setShowConfirmMergeWhenReady(true)
    handleConfirmingMergeInfo(true)
    if (shouldSendClickAnalyticsEvent) {
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'merge_box',
          action: 'stack_merge_click',
          data: {
            stack: stackMetadata,
            merge_method: selectedMergeQueueOption,
            stack_size: stackSize,
            position_in_stack: stackPositionInStack,
            uses_merge_queue: true,
          },
        }),
      )
    }
    sendAnalyticsEvent('auto_merge_section.merge_click', 'MERGEBOX_AUTO_MERGE_BUTTON')
  }

  const createAutoMergeRequest = () => {
    if (isPending) return

    setRuleErrors([])
    setErrorMessage(undefined)

    // todo: add jump method along with permission checks for each option
    const mergeQueueMethod = selectedMergeQueueOption === MergeQueueMethod.SOLO ? 'SOLO' : 'GROUP'
    if (isStacked) {
      enqueueStackMutation({mergeMethod: mergeQueueMethod})
    } else {
      enableAutoMergeMutation({mergeMethod: mergeQueueMethod})
    }
    handleConfirmingMergeInfo(false)
    sendAnalyticsEvent('auto_merge_section.confirm_direct_merge', 'MERGEBOX_AUTO_MERGE_CONFIRMATION_BUTTON')
  }

  const updateMergeQueueMethod = (newMergeQueueMethod: MergeQueueMethod, analyticsEvent: string) => {
    setSelectedMergeQueueOption(newMergeQueueMethod)
    sendAnalyticsEvent(analyticsEvent, 'MERGEBOX_MERGE_QUEUE_SECTION_MERGE_METHOD_MENU_ITEM')
  }

  const isMergeable = calculateMergeability(mergeRequirementsState, status, mergeStateStatus)
  const buttonIsPrimary = isMergeable && mergeBoxRollupStatus === 'ALL_PASSED'
  const mergeWhenReadyDisabled =
    !viewerCanAddAndRemoveFromMergeQueue ||
    mergeStateStatus === 'UNKNOWN' ||
    isDraft ||
    isInMergeQueue ||
    mergeCurrentlyBlockedForStacks ||
    !!autoMergeRequest

  const inactiveTooltipText = isStacked
    ? mergeRequirementsState === 'UNKNOWN'
      ? 'Checking for the ability to merge stack'
      : 'Merging is blocked due to failing stack merge requirements'
    : mergeRequirementsState === 'UNKNOWN'
      ? 'Checking for the ability to merge automatically'
      : 'Merging is blocked due to failing merge requirements'

  const defaultBranchName = baseRefName
  const mergeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (showConfirmMergeWhenReady && mergeButtonRef?.current) {
      mergeButtonRef?.current.focus()
    }
  }, [showConfirmMergeWhenReady])

  const buttonCount = isStacked ? stackedEntriesToMergeCount : undefined

  return (
    <>
      {ruleErrors.length > 0 && (
        <FlashError
          prefix=""
          helpUrl=""
          errorMessageNotUsingPrefix="Repository rule violations found"
          hideRuleErrorsTitle
          ruleErrors={ruleErrors}
        />
      )}
      {errorMessage && (
        <Flash className="tmp-mb-3" variant="danger">
          <Octicon className="mr-2" icon={StopIcon} />
          {errorMessage}
        </Flash>
      )}
      {showConfirmMergeWhenReady ? (
        <MergeSectionActions inline>
          <MergeSectionActions.Slot>
            <Button
              ref={mergeButtonRef}
              count={buttonCount}
              variant="primary"
              loading={isPending}
              loadingAnnouncement={mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: true,
                inProgress: true,
                isStacked,
              })}
              onClick={createAutoMergeRequest}
            >
              {mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: true,
                inProgress: false,
                isStacked,
              })}
            </Button>
          </MergeSectionActions.Slot>
          <MergeSectionActions.Slot>
            <Button
              onClick={() => {
                handleConfirmingMergeInfo(false)
                setShowConfirmMergeWhenReady(false)
                sendAnalyticsEvent(
                  'auto_merge_section.cancel_auto_merge',
                  'MERGEBOX_AUTO_MERGE_CANCEL_CONFIRMATION_BUTTON',
                )
              }}
            >
              Cancel
            </Button>
          </MergeSectionActions.Slot>
        </MergeSectionActions>
      ) : (
        <MergeSectionActions>
          <MergeSectionActions.Slot>
            <ButtonWithDropdown
              count={buttonCount}
              inactive={mergeWhenReadyDisabled}
              inactiveTooltipText={inactiveTooltipText}
              inactiveTooltipDirection="se"
              // We hide the secondary button and its actions if the viewer can only merge in a group
              hideSecondaryButton={!viewerCanAddToMergeQueueSolo}
              secondaryButtonAriaLabel="Select merge queue method"
              isPrimary={buttonIsPrimary}
              shouldFocusPrimaryButton={shouldFocusPrimaryMergeButton}
              onFocusPrimaryButton={() => setShouldFocusPrimaryMergeButton(false)}
              actionList={
                <ActionList selectionVariant="single">
                  <>
                    <MergeDropdownOption
                      primaryText="Queue and merge in a group"
                      secondaryText={`This pull request will be automatically grouped with other pull requests and merged into ${defaultBranchName}.`}
                      selected={selectedMergeQueueOption === MergeQueueMethod.GROUP}
                      onSelect={() =>
                        updateMergeQueueMethod(
                          MergeQueueMethod.GROUP,
                          'merqe_queue_section.select_queue_and_merge_in_a_group',
                        )
                      }
                    />
                    <MergeDropdownOption
                      primaryText="Queue and force solo merge"
                      secondaryText={`This pull request will be merged into ${defaultBranchName} by itself.`}
                      selected={selectedMergeQueueOption === MergeQueueMethod.SOLO}
                      onSelect={() =>
                        updateMergeQueueMethod(
                          MergeQueueMethod.SOLO,
                          'merqe_queue_section.select_queue_and_force_solo_merge',
                        )
                      }
                    />
                  </>
                </ActionList>
              }
              onPrimaryButtonClick={enqueuePullRequest}
            >
              {mergeQueueButtonText({
                mergeMethod: selectedMergeQueueOption,
                confirming: false,
                inProgress: isPending,
                isStacked,
              })}
            </ButtonWithDropdown>
          </MergeSectionActions.Slot>
          <MergeSectionActions.Slot>
            <span className={`f6 fgColor-muted${isSidePanel ? '' : ' pl-2'}`}>
              This repository uses the{' '}
              <Link href={mergeQueueUrl} inline>
                merge queue
              </Link>{' '}
              for all merges into the {defaultBranchName} branch.
            </span>
          </MergeSectionActions.Slot>
          {isStacked && (
            <MergeSectionActions.Slot>
              <PreviewFeedbackMenu />
            </MergeSectionActions.Slot>
          )}
        </MergeSectionActions>
      )}
    </>
  )
}

type DisableAutoMergeProps = {
  autoMergeRequest: AutoMergeRequest
  isMergeQueueEnabled?: boolean
  viewerCanDisableAutoMerge: boolean
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
}

/**
 * Renders the "disable auto merge" button when auto merge is currently enabled but the PR hasn't merged or been
 * added to the merge queue yet
 */
function DisableAutoMerge({
  autoMergeRequest,
  viewerCanDisableAutoMerge,
  isMergeQueueEnabled,
  handleConfirmingMergeInfo,
}: DisableAutoMergeProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const {mutate: disableAutoMerge, isPending} = useDisableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })

  const handleDisableAutoMerge = () => {
    if (isPending) return
    setErrorMessage(undefined)
    handleConfirmingMergeInfo(false)
    disableAutoMerge()
    sendAnalyticsEvent('auto_merge_section.disable_auto_merge', 'MERGEBOX_AUTO_MERGE_DISABLE_BUTTON')
  }

  const autoMergeVerb = autoMergeRequest?.mergeMethod.toLowerCase() ?? 'merge'
  const autoMergeMessage = isMergeQueueEnabled ? 'be added to the merge queue' : `${autoMergeVerb} automatically`

  return (
    <>
      {errorMessage && (
        <Flash className="tmp-mx-3 my-2" variant="danger">
          <Octicon className="mr-2" icon={StopIcon} />
          {errorMessage}
        </Flash>
      )}
      <MergeSectionActions>
        <MergeSectionActions.Slot>
          <Button
            disabled={!viewerCanDisableAutoMerge}
            onClick={handleDisableAutoMerge}
            loading={isPending}
            loadingAnnouncement="Disabling auto-merge"
          >
            Disable auto-merge
          </Button>
        </MergeSectionActions.Slot>
        <MergeSectionActions.Slot>
          <div className="flex-1">
            This pull request will <span className="text-semibold">{autoMergeMessage}</span> when all requirements are
            met.{' '}
            <Link inline href={AUTOMERGE_DOCS_LINK}>
              Learn more about automatically merging a pull request.
            </Link>
          </div>
        </MergeSectionActions.Slot>
      </MergeSectionActions>
    </>
  )
}

export type MergeSectionProps = {
  advisoryWorkspace: AdvisoryWorkspace
  autoMergeRequest: AutoMergeRequest
  baseRefName: string
  canUserPushToBase: boolean
  defaultCommitAuthorEmail: PullRequestMergeRequirementsPayload['defaultCommitAuthorEmail']
  commitMessageBody: PullRequestMergeRequirementsPayload['commitMessageBody']
  commitMessageHeadline: PullRequestMergeRequirementsPayload['commitMessageHeadline']
  conflictsCondition: DirectMergeActionsSectionProps['conflictsCondition']
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  headRepository: {ownerLogin: string; name: string} | null
  helpUrl: string
  id: string
  isDraft: boolean
  isConfirmingMergeInfo: boolean
  isCrossRepo: boolean
  isInMergeQueue: boolean
  isStacked?: boolean
  stackedEntriesToMergeCount?: number
  stackedEntries?: StackSectionEntryType[]
  stackMetadata?: PullRequestUserActionStack
  mergeBoxRollupStatus: MergeBoxRollupStatus
  mergeQueue: MergeQueue
  mergeRequirementsState: PullRequestMergeRequirementsState
  mergeStateStatus: MergeStateStatus
  numberOfCommits: number
  status: Status
  viewerCanAddAndRemoveFromMergeQueue: JSONAPIPullRequestPayload['viewerCanAddAndRemoveFromMergeQueue']
  viewerCanAddToMergeQueueSolo: JSONAPIPullRequestPayload['viewerCanAddToMergeQueueSolo']
  viewerCanAdminBypassMergeRequirements: boolean
  viewerCanDisableAutoMerge: JSONAPIPullRequestPayload['viewerCanDisableAutoMerge']
  viewerCanEnableAutoMerge: JSONAPIPullRequestPayload['viewerCanEnableAutoMerge']
  viewerMergeActions: ViewerMergeActions
  possibleCommitAuthorEmails: PullRequestMergeRequirementsPayload['possibleCommitAuthorEmails']
} & MergeButtonFocusProps

/**
 * Renders the merge actions section of the merge box, specifically the merge button
 * Depending on configurations, a viewer can select a merge method, merge directly, or add the pull request to the merge queue
 */
export function MergeSection({
  advisoryWorkspace,
  autoMergeRequest,
  handleConfirmingMergeInfo,
  mergeCurrentlyBlockedForStacks,
  id,
  isConfirmingMergeInfo,
  isInMergeQueue,
  mergeQueue,
  mergeStateStatus,
  viewerCanAddAndRemoveFromMergeQueue,
  viewerCanAddToMergeQueueSolo,
  viewerCanDisableAutoMerge,
  viewerCanEnableAutoMerge,
  viewerCanAdminBypassMergeRequirements,
  isStacked,
  stackedEntries,
  stackMetadata,
  ...rest
}: MergeSectionProps & MergeButtonFocusProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const isSidePanel = useIsMergeBoxSidePanel()
  const [isAdminBypassToggleChecked, setIsAdminBypassToggleChecked] = useState(false)
  const viewerMergeActions = rest.viewerMergeActions
  const mergeQueueMergeAction = viewerMergeActions.find(({name}) => name === MergeAction.MERGE_QUEUE)
  const directMergeAction = viewerMergeActions.find(action => action.name === MergeAction.DIRECT_MERGE)

  const isMergeable = calculateMergeability(rest.mergeRequirementsState, rest.status, mergeStateStatus)
  useEffect(() => {
    if (isMergeable) {
      sendAnalyticsEvent('merge_section.view_ready_to_merge', 'MERGEBOX_MERGE_SECTION', {pullRequestId: id})
    }
  }, [isMergeable, sendAnalyticsEvent, id])
  const isMergeQueueEnabled = mergeQueueMergeAction?.allowableStatus === 'ALLOWED'
  const isDirectMergeEnabled = directMergeAction?.allowableStatus === 'ALLOWED'
  const isAutoMergeActive = !!autoMergeRequest
  const stackSize = stackedEntries?.length
  const stackPosition = stackedEntries?.findIndex(entry => entry.position === 'CURRENT')
  const stackPositionInStack = stackPosition !== undefined && stackPosition >= 0 ? stackPosition + 1 : undefined

  // If an AutoMerge request is present, we do not show the bypass checkbox.
  // When merge queue is enabled, the ability to bypass the merge requirements does not depend on mergeability state.
  // We check for mergeability, for cases where mergeability is dependent on Status Checks
  // and the checkbox should respond to live updates of Status Checks
  const showAllowableToBypass =
    viewerCanAdminBypassMergeRequirements && !isAutoMergeActive && (!!isMergeQueueEnabled || !isMergeable)
  // We check for mergeability, for cases where mergeability is dependent on Status Checks
  // and the checkbox should respond to live updates of Status Checks.
  // Auto-merge is not supported for Stacked PRs, so we never offer it for them.
  const showIsAutoMerge = viewerCanEnableAutoMerge && !isMergeable && !isStacked
  // don't render if in an advisoryWorkspace
  if (advisoryWorkspace) return null

  let mergeComponent
  if (isAutoMergeActive) {
    // Auto merge is enabled because a request is present
    mergeComponent = (
      <DisableAutoMerge
        autoMergeRequest={autoMergeRequest}
        isMergeQueueEnabled={isMergeQueueEnabled}
        viewerCanDisableAutoMerge={viewerCanDisableAutoMerge}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
      />
    )
  } else if (!isMergeQueueEnabled && !isDirectMergeEnabled) {
    // Inactive state
    mergeComponent = (
      <DirectMergeActionsSection
        mergeable={false}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        isAdminBypassToggleChecked={false}
        isAdminBypassToggleVisible={false}
        isConfirmingMergeInfo={isConfirmingMergeInfo}
        isStacked={isStacked}
        stackMetadata={stackMetadata}
        stackPositionInStack={stackPositionInStack}
        stackSize={stackSize}
        {...rest}
      />
    )
  } else if (isMergeQueueEnabled && showAllowableToBypass && isAdminBypassToggleChecked) {
    // Merge queue is enabled, but the user can bypass merge queue and did bypass merge queue, so we let them direct merge
    mergeComponent = (
      <DirectMergeActionsSection
        mergeable={isMergeable}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        isAdminBypassToggleVisible={showAllowableToBypass}
        isAdminBypassToggleChecked
        isConfirmingMergeInfo={isConfirmingMergeInfo}
        isStacked={isStacked}
        stackMetadata={stackMetadata}
        stackPositionInStack={stackPositionInStack}
        stackSize={stackSize}
        {...rest}
      />
    )
  } else if (isMergeQueueEnabled) {
    // Merge queue is enabled but the user hasn't elected to bypass it (or possibly cannot bypass it)
    mergeComponent = (
      <AddToMergeQueueSection
        autoMergeRequest={autoMergeRequest}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
        isInMergeQueue={isInMergeQueue}
        mergeQueue={mergeQueue}
        mergeStateStatus={mergeStateStatus}
        viewerCanAddAndRemoveFromMergeQueue={viewerCanAddAndRemoveFromMergeQueue}
        viewerCanAddToMergeQueueSolo={viewerCanAddToMergeQueueSolo}
        isStacked={isStacked}
        stackMetadata={stackMetadata}
        stackPositionInStack={stackPositionInStack}
        stackSize={stackSize}
        {...rest}
      />
    )
  } else if (viewerCanEnableAutoMerge && !isAdminBypassToggleChecked && !isStacked) {
    // Auto merge is allowed and the user hasn't bypassed it to direct merge.
    // Stacked PRs do not support auto-merge, so they fall through to the disabled merge button below.
    mergeComponent = (
      <DirectMergeActionsSection
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        mergeable={isMergeable}
        isAdminBypassToggleChecked={false}
        isAdminBypassToggleVisible={showAllowableToBypass}
        isAutoMergeAllowed={showIsAutoMerge}
        isConfirmingMergeInfo={isConfirmingMergeInfo}
        isStacked={isStacked}
        stackMetadata={stackMetadata}
        stackPositionInStack={stackPositionInStack}
        stackSize={stackSize}
        {...rest}
      />
    )
  } else {
    mergeComponent = (
      <DirectMergeActionsSection
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        isAdminBypassToggleChecked={isAdminBypassToggleChecked}
        isAdminBypassToggleVisible={showAllowableToBypass}
        isConfirmingMergeInfo={isConfirmingMergeInfo}
        mergeable={isMergeable}
        isStacked={isStacked}
        stackMetadata={stackMetadata}
        stackPositionInStack={stackPositionInStack}
        stackSize={stackSize}
        {...rest}
      />
    )
  }

  return (
    <div
      className={clsx(
        isSidePanel ? 'tmp-pt-3 tmp-px-3' : 'bgColor-muted borderColor-muted tmp-p-3',
        isConfirmingMergeInfo ? 'rounded-2' : 'rounded-bottom-2',
      )}
    >
      {showAllowableToBypass && !isConfirmingMergeInfo && (
        <div className="tmp-mb-3">
          <BypassMergeRequirementsToggle
            checked={isAdminBypassToggleChecked}
            onToggleChecked={() => setIsAdminBypassToggleChecked(!isAdminBypassToggleChecked)}
          />
        </div>
      )}
      {mergeComponent}
    </div>
  )
}
